import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from "firebase/firestore";
import { ArrowLeft, FileText, MapPin } from 'lucide-react';

// --- HELPER: Format Address Object to String ---
const formatAddress = (addr) => {
  if (!addr) return 'No Address Selected';
  return `${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
};

const ProformaInvoice = ({ db }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [quote, setQuote] = useState(null);
  const [customer, setCustomer] = useState(null); 
  const [loading, setLoading] = useState(true);

  // --- Data Fetching ---
  useEffect(() => {
    const fetchQuoteData = async () => {
      try {
        const quoteRef = doc(db, "quotes", id);
        const quoteSnap = await getDoc(quoteRef);

        if (quoteSnap.exists()) {
          const quoteData = { id: quoteSnap.id, ...quoteSnap.data() };
          setQuote(quoteData);
          
          if (quoteData.customerID) {
              const customerRef = doc(db, "customers", quoteData.customerID);
              const customerSnap = await getDoc(customerRef);
              if (customerSnap.exists()) {
                  setCustomer({ id: customerSnap.id, ...customerSnap.data() });
              }
          }
          
        } else {
          console.error("No such quote document!");
        }
      } catch (error) {
        console.error("Error fetching quote: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuoteData();
  }, [db, id]);


  if (loading) return <div style={{ padding: '20px' }}>Loading Proforma Invoice...</div>;
  if (!quote) return <div style={{ padding: '20px' }}>Quote not found.</div>;
  
  const handlePrint = () => {
      window.print();
  };

  const getTotalTaxableValue = () => {
    return quote.items.reduce((sum, item) => {
      const baseAmount = item.qty * item.rate;
      const discountedBase = baseAmount - (baseAmount * (item.discount / 100));
      return sum + discountedBase;
    }, 0);
  };

  const getTotalGSTAmount = () => {
    return quote.items.reduce((sum, item) => {
      const baseAmount = item.qty * item.rate;
      const discountedBase = baseAmount - (baseAmount * (item.discount / 100));
      const gstAmount = discountedBase * (item.tax / 100);
      return sum + gstAmount;
    }, 0);
  };


  return (
    <div id="quote-print-container" style={{ 
      maxWidth: '850px', 
      margin: '0 auto', 
      paddingBottom: '100px', 
      padding: isMobile ? '10px' : '20px',
      width: isMobile ? '100%' : 'auto',
      boxSizing: 'border-box'
    }}>
      
      {GlobalPrintStyles()} 

      {/* HEADER: Back Button and Print (Hidden in print) */}
      <div className="no-print" style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: '20px',
        gap: isMobile ? '15px' : '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => navigate(-1)} style={backButtonStyle}><ArrowLeft size={20} /></button>
          <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px' }}>Proforma Invoice #{quote.quoteNumber}</h2>
        </div>
        <button onClick={handlePrint} style={{ ...pdfButtonStyle, width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}>
          <FileText size={18} /> Print / Save as PDF
        </button>
      </div>

      {/* --- PRINTABLE CONTENT STARTS HERE (The Actual PI) --- */}
      <div style={{ 
        padding: isMobile ? '10px' : '20px', 
        border: isMobile ? 'none' : '1px solid #ddd', 
        backgroundColor: 'white',
        borderRadius: isMobile ? '0' : '4px',
        width: isMobile ? 'calc(100% + 20px)' : '100%',
        margin: isMobile ? '0 -10px' : '0',
        overflowX: isMobile ? 'auto' : 'visible'
      }}>
        <div style={{ minWidth: isMobile ? '800px' : 'auto' }}>
          {/* Company Header */}
          <div style={headerPrintStyle}>
              <h1 style={{margin: '0', fontSize: '24px', color: '#1f2937'}}>NEXRISE INDIA INFRA PVT. LTD.</h1>
              <p style={{margin: '5px 0 10px 0', fontSize: '12px', color: '#4b5563'}}>Jaipur, serving across pan India. Contact: 91 9009006794 | sales@nexriseindia.in</p>
              <h2 style={{ margin: 0, fontSize: '16px', padding: '5px 0', borderTop: '1px solid #333' }}>PROFORMA INVOICE / QUOTATION</h2>
              <div style={{textAlign: 'right', fontSize: '12px', marginTop: '10px'}}>
                  <p style={{margin:0}}>**Reference: {quote.quoteNumber}**</p>
                  <p style={{margin:0}}>Date: {new Date(quote.date).toLocaleDateString()}</p>
              </div>
          </div>

          {/* --- ADDRESSES AND DETAILS GRID --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '15px' }}>
            
            {/* BILLING PARTY */}
            <div style={addressBoxStyle}>
              <h4 style={addressHeaderStyle}><MapPin size={14} style={{verticalAlign: 'middle', marginRight: '5px'}}/> BILL TO (CUSTOMER)</h4>
              <h3 style={{margin: '5px 0 10px 0'}}>{quote.customerName}</h3>
              <p style={{margin:'0', fontSize:'13px'}}>{formatAddress(quote.billingAddress)}</p>
              <div style={{marginTop: '10px', fontSize:'12px'}}>
                  <p style={{margin: '0'}}>**GSTIN:** {customer?.gstin || '--'}</p>
                  <p style={{margin: '0'}}>**PAN:** {customer?.pan || '--'}</p>
                  <p style={{margin: '0'}}>**Contact:** {quote.billingAddress?.phone || customer?.mobile || '--'}</p>
              </div>
            </div>

            {/* SHIPPING PARTY */}
            <div style={addressBoxStyle}>
              <h4 style={addressHeaderStyle}><MapPin size={14} style={{verticalAlign: 'middle', marginRight: '5px'}}/> SHIP TO (SITE)</h4>
              <h3 style={{margin: '5px 0 10px 0'}}>{quote.shippingAddress?.attention || quote.customerName}</h3>
              <p style={{margin:'0', fontSize:'13px'}}>{formatAddress(quote.shippingAddress)}</p>
              <div style={{marginTop: '10px', fontSize:'12px'}}>
                  <p style={{margin: '0'}}>**Subject:** {quote.subject}</p>
                  <p style={{margin: '0'}}>**Site Contact:** {quote.shippingAddress?.phone || customer?.mobile || '--'}</p>
                  <p style={{margin: '0'}}>**Salesperson:** {quote.salesPerson}</p>
              </div>
            </div>
          </div>
          
          {/* --- ITEMS TABLE --- */}
          <div style={tableContainerStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={{...thStyle, width: '40%', textAlign: 'left'}}>Description of Goods/Services</th>
                  <th style={thStyle}>HSN/SAC</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>Rate</th>
                  <th style={thStyle}>Taxable Amt</th>
                  <th style={thStyle}>GST @{quote.items[0]?.tax || 18}%</th>
                  <th style={thStyle}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item, index) => {
                    const baseAmount = item.qty * item.rate;
                    const discountedBase = baseAmount - (baseAmount * (item.discount / 100));
                    const gstAmount = discountedBase * (item.tax / 100);
                    const totalAmount = discountedBase + gstAmount;
                    
                    return (
                        <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={tdStyle}>{index + 1}</td>
                            <td style={{...tdStyle, textAlign: 'left', fontWeight: 'bold'}}>
                                {item.description}
                                <div style={{fontSize:'11px', color:'#6b7280', fontWeight: 'normal'}}>{item.sku}</div>
                            </td>
                            <td style={tdStyle}>{item.hsn}</td>
                            <td style={tdStyle}>{item.qty}</td>
                            <td style={tdStyle}>₹{item.rate.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td style={tdStyle}>₹{discountedBase.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td style={tdStyle}>₹{gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td style={{...tdStyle, fontWeight:'bold'}}>₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        </tr>
                    )})}
              </tbody>
            </table>

            {/* SUMMARY ROW */}
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ width: '350px', fontSize: '14px' }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Sub Total:</span>
                      <span>₹{getTotalTaxableValue().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', borderBottom: '1px solid #ddd' }}>
                      <span>Total GST:</span>
                      <span>₹{getTotalGSTAmount().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px', paddingTop: '5px' }}>
                      <span>Grand Total:</span>
                      <span>₹{quote.grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                   </div>
                </div>
            </div>
          </div>

          {/* --- TERMS AND CONDITIONS & BANK DETAILS --- */}
          <div style={{ marginTop: '30px', borderTop: '1px solid #ddd', paddingTop: '20px', fontSize: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
                  
                  {/* TERMS SECTION */}
                  <div>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Terms & Conditions</h4>
                      <ol style={{ paddingLeft: '18px', margin: 0 }}>
                          {quote.terms?.map((term, index) => (
                              <li key={index} style={{ marginBottom: '5px', lineHeight: '1.4' }}>
                                  {term}
                              </li>
                          ))}
                      </ol>
                  </div>
                  
                  {/* BANK DETAILS SECTION */}
                  <div style={bankDetailStyle}>
                      <p style={{ fontWeight: 'bold', margin: '0 0 10px 0' }}>Bank Details (For {quote.firmId.toUpperCase()}):</p>
                      <p style={{ margin: '0' }}>A/C Name: NEXRISE INDIA INFRA PVT. LTD.</p> 
                      <p style={{ margin: '0' }}>Bank: HDFC Bank</p>
                      <p style={{ margin: '0' }}>IFSC: HDFC0001234</p>
                      <p style={{ margin: '0' }}>A/C No: 50200012345678</p> 
                  </div>
              </div>
          </div>

          {/* Signature Line */}
          <div style={signBoxStyle}>
             <p style={{margin: 0}}>For **NEXRISE INDIA INFRA PVT. LTD.**</p>
             <p style={{margin: '40px 0 0 0', fontWeight: 'bold'}}>&lt;Authorized Signatory&gt;</p>
          </div>
        </div>
      </div> 
      
    </div>
  );
};

// --- STYLES (Used by both PI and print CSS) ---
const addressHeaderStyle = { margin: '0 0 5px 0', fontSize: '14px', color: '#333' };
const addressBoxStyle = { background: '#f8fafc', padding: '15px', borderRadius: '4px' };
const headerPrintStyle = { textAlign: 'center', marginBottom: '20px', padding: '10px 0', borderBottom: '3px solid #333' };
const thStyle = { padding: '12px 5px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 5px', fontSize: '12px', textAlign: 'center' };
const tableContainerStyle = { backgroundColor: 'white', borderRadius: '4px', border: '1px solid #ddd', overflow: 'hidden' };
const backButtonStyle = { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '5px' };
const pdfButtonStyle = { backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' };
const bankDetailStyle = { border: '1px solid #ddd', padding: '15px', borderRadius: '4px', backgroundColor: '#f9fafb' };
const signBoxStyle = { borderTop: '1px solid #333', marginTop: '40px', paddingTop: '10px', textAlign: 'right', fontSize: '13px' };

// --- GLOBAL PRINT STYLES (REQUIRED FOR PDF GENERATION) ---
const GlobalPrintStyles = () => (
    <style>
      {`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; background: #fff; }
          @page { size: A4; margin: 0.5in; }
          #quote-print-container { width: 100%; margin: 0; box-shadow: none; padding: 0 !important; }
          /* Enforce styles for printing */
          h1, h2, h3, h4, p, div, table { color: #000 !important; }
          .no-margin { margin: 0 !important; }
          
          /* Ensure table elements don't break across pages poorly */
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      `}
    </style>
);
// --- END GLOBAL PRINT STYLES ---


export default ProformaInvoice;